/**
 * Manual invite code entry.
 *
 * Kept as a fallback for the case the documented path cannot cover: the invite
 * mail links to `/me/invite?code=…`, but a mail client may rewrite that link, in
 * which case the code has to be typed by hand. Failures are shown instead of
 * being swallowed: a wrong code is the user's only signal about what went wrong.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorAlert } from '../layout/states'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Field, FieldGroup } from '../ui/field'
import { Input } from '../ui/input'
import { useRedeemInvite } from '../../hooks/use-invite'
import { mutationErrorText } from '../../lib/mutation-error'

export function RedeemForm({ initialCode = '' }: { initialCode?: string }) {
  const { t } = useTranslation()
  const redeem = useRedeemInvite()
  const [code, setCode] = useState(initialCode)

  // The failure is read as text rather than as an `ApiError`: a request that never
  // reached the server has no `ApiError` shape but still needs a message.
  const redeemError = mutationErrorText(redeem.error, t)

  function submit() {
    const value = code.trim()
    if (value.length === 0) {
      return
    }
    redeem.mutate({ code: value }, { onSuccess: () => setCode('') })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('invite.redeem.title')}</CardTitle>
        <CardDescription>{t('invite.redeem.body')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          <FieldGroup>
            <Field label={t('invite.redeem.codeLabel')} htmlFor="invite-code" required>
              <Input
                id="invite-code"
                name="code"
                autoComplete="off"
                value={code}
                aria-invalid={Boolean(redeemError)}
                onChange={(event) => setCode(event.target.value)}
              />
            </Field>
          </FieldGroup>

          {redeemError ? (
            <ErrorAlert message={redeemError} />
          ) : null}

          <Button
            type="submit"
            className="self-start"
            disabled={redeem.isPending || code.trim().length === 0}
          >
            {redeem.isPending ? t('invite.redeem.submitting') : t('invite.redeem.submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
